<script lang="ts">
  import type { Snippet } from "svelte";
  import { focusTrap } from "../a11y/focusTrap";
  import { lockScroll } from "../a11y/scrollLock";

  interface Props {
    open: boolean;
    title: string;
    onclose: () => void;
    dismissable?: boolean;
    children: Snippet;
    footer?: Snippet;
  }
  const { open, title, onclose, dismissable = true, children, footer }: Props = $props();
  const titleId = "drawer-title-" + Math.random().toString(36).slice(2, 8);

  $effect(() => {
    if (open) return lockScroll();
    return undefined;
  });

  function onOverlayClick(event: MouseEvent): void {
    if (dismissable && event.target === event.currentTarget) onclose();
  }
</script>

{#if open}
  <div class="overlay" onclick={onOverlayClick} role="presentation">
    <div
      class="drawer"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      use:focusTrap={{ onEscape: dismissable ? onclose : undefined }}
    >
      <header class="drawer-head">
        <h2 id={titleId} class="drawer-title">{title}</h2>
        <button
          type="button"
          class="drawer-close"
          onclick={onclose}
          aria-label="Close panel"
          disabled={!dismissable}
          title={dismissable ? undefined : "Finish or discard changes before closing"}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
        </button>
      </header>
      <div class="drawer-body">{@render children()}</div>
      {#if footer}<footer class="drawer-foot">{@render footer()}</footer>{/if}
    </div>
  </div>
{/if}

<style>
  .overlay {
    position: fixed;
    inset: 0;
    z-index: 100;
    display: flex;
    justify-content: flex-end;
    background: var(--scrim-soft);
  }
  .drawer {
    width: min(440px, 100%);
    height: 100%;
    background: var(--surface);
    border-left: 1px solid var(--line);
    padding: 22px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    animation: drawer-in var(--dur-3) var(--ease-out);
  }
  @media (prefers-reduced-motion: reduce) {
    .drawer {
      animation: none;
    }
  }
  @keyframes drawer-in {
    from {
      transform: translateX(24px);
      opacity: 0;
    }
  }
  .drawer-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 18px;
  }
  .drawer-title {
    font-size: 17px;
  }
  .drawer-close {
    flex: none;
    width: 32px;
    height: 32px;
    display: grid;
    place-items: center;
    border-radius: 8px;
    border: 1px solid var(--line-soft);
    background: transparent;
    color: var(--muted);
    cursor: pointer;
  }
  .drawer-close:hover {
    color: var(--heading);
    border-color: var(--accent);
  }
  .drawer-close:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .drawer-close svg {
    width: 16px;
    height: 16px;
  }
  .drawer-body {
    flex: 1 1 auto;
  }
  .drawer-foot {
    margin-top: 18px;
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
</style>
